import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Tenant, TenantFormData, TenantAdminInvitation, TenantDeletionRequest, TenantStatus } from "@/types/tenant";
import { useToast } from "@/hooks/use-toast";
import { apiGet, apiPost, apiPut } from "@/services/apiClient";

export function useTenants() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const tenantsQuery = useQuery({
    queryKey: ["tenants"],
    queryFn: async (): Promise<Tenant[]> => {
      try {
        const data = await apiGet<any>('/api/v1/tenants', { includeTenantHeader: true });
        return (data?.data || []).map((t: any) => ({ ...t, tenant_status: t.tenant_status || 'active', device_count: t.device_count || 0 }));
      } catch { return []; }
    },
  });

  const tenantByIdQuery = (id: string) => useQuery({
    queryKey: ["tenant", id],
    queryFn: async (): Promise<Tenant | null> => {
      try {
        const data = await apiGet<any>(`/api/v1/tenants/${id}`, { includeTenantHeader: true });
        return data?.data || null;
      } catch { return null; }
    },
    enabled: !!id,
  });

  const createTenantMutation = useMutation({
    mutationFn: async (formData: TenantFormData) => {
      return await apiPost<any>('/api/v1/tenants', formData, { includeTenantHeader: true });
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["tenants"] }); toast({ title: "Tenant Created" }); },
    onError: (error) => { toast({ title: "Error", description: error.message, variant: "destructive" }); },
  });

  const updateTenantMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<TenantFormData> }) => {
      return await apiPut<any>(`/api/v1/tenants/${id}`, data, { includeTenantHeader: true });
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["tenants"] }); toast({ title: "Tenant Updated" }); },
    onError: (error) => { toast({ title: "Error", description: error.message, variant: "destructive" }); },
  });

  const updateTenantStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: TenantStatus }) => {
      return await apiPut<any>(`/api/v1/tenants/${id}/status`, { status }, { includeTenantHeader: true });
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["tenants"] }); },
  });

  const licenseUsageQuery = useQuery({
    queryKey: ["tenant-license-usage"],
    queryFn: async () => ({ totalDevices: 0, activeTenants: 0, pendingTenants: 0, tenantUsage: [] }),
  });

  return {
    tenants: tenantsQuery.data || [], isLoading: tenantsQuery.isLoading, error: tenantsQuery.error, refetch: tenantsQuery.refetch,
    getTenantById: tenantByIdQuery,
    createTenant: createTenantMutation.mutateAsync, isCreating: createTenantMutation.isPending,
    updateTenant: updateTenantMutation.mutateAsync, isUpdating: updateTenantMutation.isPending,
    updateTenantStatus: updateTenantStatusMutation.mutateAsync,
    licenseUsage: licenseUsageQuery.data, isLoadingLicenseUsage: licenseUsageQuery.isLoading,
  };
}

export function useTenantInvitations(_customerId?: string) {
  return { invitations: [] as TenantAdminInvitation[], isLoading: false, cancelInvitation: async (_id: string) => {} };
}

export function useTenantDeletions() {
  return { deletions: [] as TenantDeletionRequest[], isLoading: false, requestDeletion: async (_params: any) => {}, isRequesting: false };
}
